import { Permissions } from '../../../../../store/reducers/user';
import { getConfig } from '../../../../../util/configUtil';
import { PATIENT_CONTEXT_TYPE } from '../../../../../util/patientContextUtil';
import Links from '../../../../../components/header/links';
import ClinicalNotesComponent from '../../../../../components/notes/clinicalNotes';
import Observations from '../../../../ward/forms/observation/observations';
import SepsisPathwayTableView from '../../../../ward/sepsis/sepsisPathway';
import Forms from '../../../../ward/forms/forms';
import DNACPRTableView from '../../../../ward/forms/dnacpr/dnacpr_table';
import InpatientHistoryList from '../../../../ward/history/inpatientHistoryList';
import AttendanceCollection from '../../../../ward/attendanceCollection';
import { merge } from 'lodash';
import MainScreen from '../../../../ward/mainScreen/mainScreen';
import ICE from '../../../../ward/forms/ice/ice';
import PatientTimeline from '../../../../timeline/patientTimeline';
import AssessmentList from '../../../../ward/forms/assessment/assessmentList';
import { PATIENT_CONTEXT_TABS_ID } from '../../../../ward/forms/formsUtil';
import EmptyTab from '../emptyTab';
import PatientContextScheduler from '../../../../ward/forms/scheduler/scheduler';
import React from 'react';
import { PATIENT_TAGS } from '../../../../../util/patientUtil';
import NoteDocumentRender from '../../../../../components/notes/component/attachments/noteDocumentRender';

const AdmitPatient = React.lazy(() => import('../../../../admit/admitPatient'));

const defaultOptions = {
    hideScroll: false,
    iframe: false,
    showOnTab: true,
    hideOnPastActivity: false,
    disableForPatientTags: [],
    disabledForPermissions: [],
};

const contextItem = (
    id,
    context,
    tabDescription,
    order,
    enabled = true,
    requiredPermission = [],
    excludePatientType = [],
    options = {},
) => {
    const mergedOpt = merge({}, defaultOptions, options);
    return { id, context, tabDescription, order, enabled, requiredPermission, excludePatientType, options: mergedOpt };
};

const contextItems = [
    contextItem(PATIENT_CONTEXT_TABS_ID.PATIENT, <MainScreen />, 'Patient', 1, true, [], [], {
        hideScroll: true,
        disabledForPermissions: [Permissions.AGYLE_DEMO],
    }),
    contextItem(PATIENT_CONTEXT_TABS_ID.NOTES, <ClinicalNotesComponent />, 'Clinical Notes', 2, true, [
        Permissions.AGYLE_CAN_ACCESS_CLINICAL_NOTES,
        Permissions.AGYLE_CAN_ADD_CLINICAL_NOTES,
        Permissions.AGYLE_CAN_EDIT_AND_SET_INCORRECT_CLINICAL_NOTES,
    ]),
    contextItem(
        PATIENT_CONTEXT_TABS_ID.OBSERVATIONS,
        <Observations />,
        null,
        3,
        true,
        [Permissions.AGYLE_CAN_VIEW_OBSERVATIONS],
        [PATIENT_CONTEXT_TYPE.REVIEW],
        { hideScroll: true },
    ),
    contextItem(PATIENT_CONTEXT_TABS_ID.ASSESSMENT, <AssessmentList />, 'Assessments', 4, true, [], [PATIENT_CONTEXT_TYPE.REVIEW], {
        disabledForPermissions: [Permissions.AGYLE_DEMO],
    }),

    contextItem(
        PATIENT_CONTEXT_TABS_ID.FORMS,
        <Forms />,
        'Forms',
        5,
        !getConfig('formsTabHidden', false),
        [Permissions.AGYLE_CAN_ACCESS_FORMS_LIBRARY],
        [PATIENT_CONTEXT_TYPE.REVIEW],
        { disableForPatientTags: [PATIENT_TAGS.TEMPORARY] },
    ),
    contextItem(PATIENT_CONTEXT_TABS_ID.DNACPR, <DNACPRTableView />, 'DNA CPR', 6, getConfig('clerkingCprVisible', false), [], [], {
        hideScroll: true,
        disabledForPermissions: [Permissions.AGYLE_DEMO],
    }),
    contextItem(
        PATIENT_CONTEXT_TABS_ID.SEPSIS,
        <SepsisPathwayTableView />,
        null,
        7,
        true,
        [Permissions.AGYLE_CAN_MANAGE_SEPSIS, Permissions.AGYLE_CAN_VIEW_SEPSIS],
        [PATIENT_CONTEXT_TYPE.REVIEW],
    ),
    contextItem(
        PATIENT_CONTEXT_TABS_ID.HISTORY,
        <InpatientHistoryList />,
        'Activity History',
        8,
        true,
        [Permissions.AGYLE_CAN_VIEW_ATTENDANCE_HISTORY, Permissions.AGYLE_CAN_EDIT_ATTENDANCE_HISTORY],
        [PATIENT_CONTEXT_TYPE.EDAU, PATIENT_CONTEXT_TYPE.REVIEW],
        { disableForPatientTags: [PATIENT_TAGS.TEMPORARY], hideOnPastActivity: true },
    ),
    contextItem(
        PATIENT_CONTEXT_TABS_ID.HISTORY_UHD,
        <AttendanceCollection />,
        'Attendance Collection',
        9,
        getConfig('enableAttendancesTableTab', false),
        [Permissions.AGYLE_CAN_VIEW_ATTENDANCE_HISTORY, Permissions.AGYLE_CAN_EDIT_ATTENDANCE_HISTORY],
        [PATIENT_CONTEXT_TYPE.EDAU],
        { disableForPatientTags: [PATIENT_TAGS.TEMPORARY], disabledForPermissions: [Permissions.AGYLE_DEMO] },
    ),
    contextItem(
        PATIENT_CONTEXT_TABS_ID.UCR,
        null,
        getConfig('ucrTabName', 'Documents'),
        10,
        !getConfig('ucrTabHidden', false),
        [Permissions.AGYLE_CAN_VIEW_DPR_TAB],
        [],
        { iframe: true, disableForPatientTags: [PATIENT_TAGS.TEMPORARY] },
    ),
    contextItem(PATIENT_CONTEXT_TABS_ID.LINKS, <Links />, 'Links', 11, true),
    contextItem(
        PATIENT_CONTEXT_TABS_ID.UCR_FILTERED,
        null,
        'Clinical Letters',
        12,
        !getConfig('clinicalLetterTabHidden', false),
        [Permissions.AGYLE_CAN_VIEW_CLINICAL_LETTERS],
        [],
        { iframe: true, disableForPatientTags: [PATIENT_TAGS.TEMPORARY] },
    ),
    contextItem(PATIENT_CONTEXT_TABS_ID.ICE, <ICE />, 'Reports', 13, true, [Permissions.AGYLE_CAN_VIEW_ICE_REPORTS_INFO], [], {
        disableForPatientTags: [PATIENT_TAGS.TEMPORARY],
    }),
    contextItem(PATIENT_CONTEXT_TABS_ID.TIMELINE, <PatientTimeline />, null, 14, true, [], [], { showOnTab: false }),
    contextItem(
        PATIENT_CONTEXT_TABS_ID.SCHEDULER,
        <PatientContextScheduler />,
        'Scheduler',
        14,
        getConfig('modules.scheduler.enabled', false),
        [Permissions.AGYLE_CAN_ACCESS_SCHEDULER],
        [],
        { disableForPatientTags: [PATIENT_TAGS.TEMPORARY] },
    ),
    contextItem(PATIENT_CONTEXT_TABS_ID.EMPTY, <EmptyTab />, null, 15, true, [], [], { showOnTab: false }),
    contextItem(
        PATIENT_CONTEXT_TABS_ID.REGISTRY,
        <AdmitPatient fromContext />,
        null,
        16,
        getConfig('admitPatient.registration', false),
        [Permissions.AGYLE_CAN_ADMIT_PATIENT],
        [],
        { showOnTab: false },
    ),
    contextItem(PATIENT_CONTEXT_TABS_ID.NOTE_DOCUMENT, <NoteDocumentRender />, null, 17, true, [], [], { showOnTab: false }),
];

export const PATIENT_CONTEXT_ITEMS = contextItems.sort((a, b) => a.order - b.order);
